import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { BillData } from '@shared/schema/billData';
import type { FieldValidationError } from '@shared/schema/validationError';
import type { PreparedImage } from '@/services/image/prepareImage';

/**
 * Holds the in-progress scan (captured photo + extraction result) across the
 * Scanner -> Preview -> Processing -> Review route sequence, for the
 * duration of one scan. Once the user confirms on Review, the result is
 * persisted to IndexedDB (lib/db/billsRepo.ts) as durable history — this
 * context is deliberately just in-memory scratch space for one active scan.
 */
interface ScanSessionState {
  prepared: PreparedImage | null;
  previewUrl: string | null;
  billData: BillData | null;
  warnings: FieldValidationError[];
}

interface ScanSessionContextValue extends ScanSessionState {
  setPrepared: (prepared: PreparedImage) => void;
  setExtractionResult: (billData: BillData, warnings: FieldValidationError[]) => void;
  updateBillData: (billData: BillData) => void;
  reset: () => void;
}

const ScanSessionContext = createContext<ScanSessionContextValue | null>(null);

const emptyState: ScanSessionState = { prepared: null, previewUrl: null, billData: null, warnings: [] };

export function ScanSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ScanSessionState>(emptyState);
  const previewUrlRef = useRef<string | null>(null);

  const setPrepared = useCallback((prepared: PreparedImage) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const previewUrl = URL.createObjectURL(prepared.original);
    previewUrlRef.current = previewUrl;
    setState({ prepared, previewUrl, billData: null, warnings: [] });
  }, []);

  const setExtractionResult = useCallback((billData: BillData, warnings: FieldValidationError[]) => {
    setState((prev) => ({ ...prev, billData, warnings }));
  }, []);

  const updateBillData = useCallback((billData: BillData) => {
    setState((prev) => ({ ...prev, billData }));
  }, []);

  const reset = useCallback(() => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setState(emptyState);
  }, []);

  const value = useMemo<ScanSessionContextValue>(
    () => ({ ...state, setPrepared, setExtractionResult, updateBillData, reset }),
    [state, setPrepared, setExtractionResult, updateBillData, reset],
  );

  return <ScanSessionContext.Provider value={value}>{children}</ScanSessionContext.Provider>;
}

export function useScanSession(): ScanSessionContextValue {
  const ctx = useContext(ScanSessionContext);
  if (!ctx) throw new Error('useScanSession must be used within a ScanSessionProvider');
  return ctx;
}
